import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  memo,
} from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { InputField } from "../../../../Components/formElements/InputField";
import { useDispatch, useSelector } from "react-redux";
import { getSupplierList } from "../../../../redux/feature/supplier_app/supplierSlice";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import FileUploadComponent from "../../../../Components/formElements/FileUploadComponent";
import FileViewerModal from "../../../../Components/FileViewer/FileViewerModal";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
// import { ComboboxFieldSubArray } from "../../../../Components/formElements/ComboboxFieldSubArray";

// ─────────────────────────────────────────────────────────────────────────────
// Module-level constants — created once, never trigger re-renders
// ─────────────────────────────────────────────────────────────────────────────
const SERVICE_STATUS = {
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
};

const serviceStatusOptions = [
  { value: SERVICE_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: SERVICE_STATUS.COMPLETED, label: "Completed" },
];

const issuesReportedData = [
  { name: "Not working", code: "NW", description: "Not working" },
  { name: "Software Issue", code: "SI", description: "Software Issue" },
];

const serviceInfoInitialData = {
  serviceId: "",
  serviceDate: "",
  serviceCenter: "",
  serviceType: "",
  odometerReading: "",
  issuesReported: "",
  issuesDetails: "",
  workPerformed: "",
  partsReplaced: "",
  totalCost: "",
  nextServiceDue: "",
  invoiceNumber: "",
  serviceCompletedDate: "",
  remarks: "",
  // Multi-select equipment fields
  linkedLocalIds: [],
  linkedRentItems: [],
  linkedLocalId: null,             // first selected (backward compat)
  linkedBarcode: "",
  linkedMaterialDescription: "",
  linkedEquipmentRentNr: "",
  serviceStatus: SERVICE_STATUS.IN_PROGRESS,
};

// ─────────────────────────────────────────────────────────────────────────────
// Static service history columns
// ─────────────────────────────────────────────────────────────────────────────
const serviceHistoryColumns = [
  {
    name: "Service ID",
    selector: (row) => row?.serviceId || "--",
    cell: (row) => (
      <span className="bg-table-status bg-table-new-status">{row.serviceId}</span>
    ),
    sortable: true,
    width: "150px",
  },
  {
    name: "Equipment",
    selector: (row) => row?.linkedMaterialDescription || "--",
    cell: (row) => (
      <div>
        <p className="m-0 p-0 fw-bold" style={{ fontSize: "12px" }}>
          {row.linkedMaterialDescription || "--"}
        </p>
        <p className="m-0 p-0 text-muted" style={{ fontSize: "11px" }}>
          {row.linkedBarcode || ""}
        </p>
      </div>
    ),
    sortable: true,
    width: "200px",
  },
  {
    name: "Service Provider",
    selector: (row) => row?.serviceProvider || "--",
    sortable: true,
    width: "180px",
  },
  {
    name: "Service Status",
    selector: (row) => row?.serviceStatus || "--",
    cell: (row) => (
      <span
        className={`badge ${row?.serviceStatus === SERVICE_STATUS.COMPLETED
            ? "bg-success"
            : "bg-warning text-dark"
          }`}
      >
        {row?.serviceStatus || "--"}
      </span>
    ),
    sortable: true,
    width: "160px",
  },
  {
    name: "Service Date/Time",
    selector: (row) => row?.serviceDate || "--",
    cell: (row) => (
      <div>
        <p className="m-0">{row?.serviceDate}</p>
        <p className="m-0 text-muted" style={{ fontSize: "11px" }}>{row?.serviceTime}</p>
      </div>
    ),
    sortable: true,
    width: "180px",
  },
  {
    name: "Service Type",
    selector: (row) => row?.serviceType || "--",
    sortable: true,
    width: "150px",
  },
  {
    name: "Issues Reported",
    selector: (row) => row?.issuesReported || "--",
    sortable: true,
    width: "180px",
  },
  {
    name: "Issues Details",
    selector: (row) => row?.issuesDetails || "--",
    sortable: true,
    width: "180px",
  },
  {
    name: "Total Cost",
    selector: (row) => row?.totalCost || "--",
    cell: (row) => <p className="m-0 p-0 fw-bold">{row.totalCost}</p>,
    sortable: true,
    width: "150px",
  },
  {
    name: "Next Service Due",
    selector: (row) => row?.nextServiceDue || "--",
    cell: (row) => (
      <p className="m-0 p-0 pt-1 text-primary fw-bold">{row.nextServiceDue}</p>
    ),
    sortable: true,
    width: "170px",
  },
  {
    name: "Work Performed",
    selector: (row) => row?.workPerformed || "--",
    sortable: true,
    width: "170px",
  },
  {
    name: "Service Completed Date",
    selector: (row) => row?.serviceCompletedDate || "--",
    sortable: true,
    width: "200px",
  },
  {
    name: "Invoice Number",
    selector: (row) => row?.invoiceNumber || "--",
    sortable: true,
    width: "160px",
  },
  {
    name: "Remarks",
    selector: (row) => row?.remarks || "--",
    sortable: true,
    width: "200px",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// ServiceForm — memoized child
// ─────────────────────────────────────────────────────────────────────────────
const ServiceForm = memo(({
  item,
  rentItems,
  editMode,
  supplierDropdownData,
  lockedLocalIds,
  onSelectRentItem,
  onDeselectRentItem,
  onChangeServiceInfo,
  onChangeManyServiceInfo,
  onRemoveService,
  onUploadSuccess,
  onDeleteAttachment,
  onViewFile,
}) => {
  const currentStatus = item?.serviceStatus || SERVICE_STATUS.IN_PROGRESS;
  const isCompleted = currentStatus === SERVICE_STATUS.COMPLETED;
  const linkedLocalIds = item?.linkedLocalIds || [];
  const hasEquipment = linkedLocalIds.length > 0;

  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        padding: "12px",
        marginBottom: "16px",
        background: "#fafafa",
      }}
    >
      <Row className="pt-2 pb-2">

        {/* ── Header ───────────────────────────────────────── */}
        <Col
          xs={12} md={12} lg={12}
          className="commTopButtonRightLeftPadding d-flex justify-content-between align-items-center mt-1 "
        >
          <h6 className="mb-0">
            Service - {item?.serviceVehicleId}
            {item?.linkedMaterialDescription && (
              <> : <span className="fw-medium text-black">{item.linkedMaterialDescription}</span></>
            )}
          </h6>
          {!item?.serviceUpdate && (
            <span
              className="deleteIconAccordion pt-2"
              onClick={() => onRemoveService(item?.serviceId)}
              style={{ cursor: "pointer" }}
            >
              <i className="bi bi-x-circle"></i>
            </span>
          )}
        </Col>

        {/* ── Service Information section ───────────────────── */}
        <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
          <span className="fw-bold text-primary">Service Information</span>
          <hr />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Service Id"
            name="serviceId"
            value={item?.serviceId}
            disabled={true}
          />
        </Col>

        {/* ── Multi-select Equipment using ComboboxFieldSubArray ── */}
        {/* <Col xs={12} md={8} lg={8} className="commTopButtonRightLeftPadding mt-2">
  <label className="form-label fw-bold">
    Select Equipment <span className="text-danger">*</span>
  </label>

  {(item?.linkedLocalIds || []).length > 0 && (
    <div
      className="d-flex flex-wrap gap-2 mb-2"
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "6px",
        padding: "6px 8px",
        background: "#f8f9fa",
      }}
    >
      {(item?.linkedLocalIds || []).map((localId) => {
        const rentItem = rentItems.find(
          (r) => String(r.local_id) === String(localId)
        );
        if (!rentItem) return null;
        return (
          <span
            key={localId}
            style={{
              background: "#e7f0ff",
              border: "1px solid #b3cfff",
              borderRadius: "4px",
              padding: "2px 8px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            {rentItem.barcode} — {rentItem.materialDescription}
            {!item?.serviceUpdate && (
              <i
                className="bi bi-x"
                style={{ cursor: "pointer", fontWeight: "bold", fontSize: "13px" }}
                onClick={() => onDeselectRentItem(item?.serviceVehicleId, localId)}
              />
            )}
          </span>
        );
      })}
    </div>
  )}

  {!item?.serviceUpdate && (
    <ComboboxFieldSubArray
      id={`equipmentSelect_${item?.serviceVehicleId}`}
      name="equipment"
      label=""
      placeholder="-- Add Equipment --"
      disabled={item?.serviceUpdate}
      data={rentItems
        .filter((r) => {
          const alreadySelected = (item?.linkedLocalIds || []).includes(String(r.local_id));
          const isLockedByOther =
            lockedLocalIds.current.has(String(r.local_id)) &&
            !(item?.linkedLocalIds || []).includes(String(r.local_id));
          return !alreadySelected && !isLockedByOther;
        })
        .map((r) => ({
          id:   r.local_id,
          name: `${r.barcode} — ${r.materialDescription}`,
          _raw: r,  // carry original for handler
        }))
      }
      setValue={null}
      getvalue={(val) => {
        if (val?.id) onSelectRentItem(item?.serviceVehicleId, val.id);
      }}
    />
  )}

  {(!item?.linkedLocalIds || item.linkedLocalIds.length === 0) ? (
    <small className="text-danger fw-bold mt-1 d-block">
      <i className="bi bi-exclamation-circle me-1"></i>
      Equipment selection required
    </small>
  ) : (
    <small className="text-success fw-bold mt-1 d-block">
      <i className="bi bi-check-circle me-1"></i>
      {item.linkedLocalIds.length} equipment selected
    </small>
  )}
</Col> */}

        {/* ── Multi-select Equipment ───────────────────────── */}
        <Col xs={12} md={8} lg={8} className="d-flex flex-column commTopButtonRightLeftPadding mt-2">
          <label className="form-label fw-bold">
            Select Equipment <span className="text-danger">*</span>
          </label>

          <div
            style={{
              border: `1px solid ${!hasEquipment ? "#dc3545" : "#dee2e6"}`,
              borderRadius: "6px",
              padding: "6px 8px",
              minHeight: "42px",
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              alignItems: "center",
              background: item?.serviceUpdate ? "#e9ecef" : "#fff",
              cursor: item?.serviceUpdate ? "not-allowed" : "default",
            }}
          >
            {/* Chips for selected equipment */}
            {linkedLocalIds.map((localId) => {
              const rentItem = rentItems.find(
                (r) => String(r.local_id) === String(localId)
              );
              if (!rentItem) return null;
              return (
                <span
                  key={localId}
                  style={{
                    background: "#e7f0ff",
                    border: "1px solid #b3cfff",
                    borderRadius: "4px",
                    padding: "2px 8px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rentItem.barcode} — {rentItem.materialDescription}
                  {!item?.serviceUpdate && (
                    <i
                      className="bi bi-x fw-medium"
                      style={{ cursor: "pointer", fontWeight: "bold", fontSize: "16px" }}
                      onClick={() => onDeselectRentItem(item?.serviceVehicleId, localId)}
                    />
                  )}
                </span>
              );
            })}

            {/* Inline dropdown to add more */}
            {!item?.serviceUpdate && (
              <select
                style={{
                  flex: 1,
                  minWidth: "160px",
                  outline: "none",
                  border: "none",
                  background: "transparent",
                  fontSize: "13px",
                  color: "#6c757d",
                  cursor: "pointer",
                }}
                value=""
                onChange={(e) => {
                  if (e.target.value) onSelectRentItem(item?.serviceVehicleId, e.target.value);
                }}
              >
                <option value="">-- Add Equipment --</option>
                {rentItems.map((rentItem) => {
                  const alreadySelected = linkedLocalIds.includes(String(rentItem.local_id));
                  const isLockedByOther =
                    lockedLocalIds.current.has(String(rentItem.local_id)) &&
                    !linkedLocalIds.includes(String(rentItem.local_id));
                  return (
                    <option
                      key={rentItem.local_id}
                      value={rentItem.local_id}
                      disabled={alreadySelected || isLockedByOther}
                    >
                      {rentItem.barcode} — {rentItem.materialDescription}
                      {alreadySelected
                        ? " (Selected)"
                        : isLockedByOther
                          ? " (In Progress)"
                          : ""}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {!hasEquipment ? (
            <small className="text-danger fw-bold mt-1 d-block">
              <i className="bi bi-exclamation-circle me-1"></i>
              Equipment selection required
            </small>
          ) : (
            <small className="text-success fw-bold mt-1 d-block">
              <i className="bi bi-check-circle me-1"></i>
              {linkedLocalIds.length} equipment selected
            </small>
          )}
        </Col>

        {/* Service Provider */}
        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <label className="form-label fw-bold">Service Provider</label>
          <select
            className="form-select"
            disabled={item?.serviceUpdate}
            value={item?.serviceProviderCode || ""}
            onChange={(e) => {
              const selected = supplierDropdownData.find((s) => s.code === e.target.value);
              onChangeManyServiceInfo(item?.serviceVehicleId, {
                serviceProvider: selected?.name || "",
                serviceProviderCode: selected?.code || "",
                serviceProviderDesc: selected?.description || "",
              });
            }}
          >
            <option value="">-- Select Service Provider --</option>
            {supplierDropdownData.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Service Date"
            name="serviceDate"
            value={item?.serviceDate || ""}
            disabled={item?.serviceUpdate}
            type="date"
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "serviceDate", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Service Time"
            name="serviceTime"
            value={item?.serviceTime || ""}
            disabled={item?.serviceUpdate}
            type="time"
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "serviceTime", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Service Type"
            name="serviceType"
            value={item?.serviceType || ""}
            disabled={item?.serviceUpdate}
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "serviceType", e.target.value)
            }
          />
        </Col>

        {/* Issues Reported */}
        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <label className="form-label fw-bold">Issues Reported</label>
          <select
            className="form-select"
            disabled={item?.serviceUpdate}
            value={item?.issuesReportedCode || ""}
            onChange={(e) => {
              const selected = issuesReportedData.find((d) => d.code === e.target.value);
              onChangeManyServiceInfo(item?.serviceVehicleId, {
                issuesReported: selected?.name || "",
                issuesReportedCode: selected?.code || "",
                issuesReportedDesc: selected?.description || "",
              });
            }}
          >
            <option value="">-- Select Issue --</option>
            {issuesReportedData.map((d) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
          </select>
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <TextAreaField
            className="inputBox"
            label="Issues Details"
            name="issuesDetails"
            value={item?.issuesDetails || ""}
            disabled={item?.serviceUpdate}
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "issuesDetails", e.target.value)
            }
          />
        </Col>

        {/* ── Service Update section ────────────────────────── */}
        <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
          <span className="fw-bold text-primary">Service Update</span>
          <hr />
        </Col>

        {/* Service Status */}
        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <label className="form-label fw-bold">
            Service Status <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            disabled={item?.serviceCompleted}
            value={currentStatus}
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "serviceStatus", e.target.value)
            }
          >
            {serviceStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <small
            className={`fw-bold mt-1 d-inline-block ${isCompleted ? "text-success" : "text-warning"
              }`}
          >
            <i
              className={`bi me-1 ${isCompleted ? "bi-check-circle-fill" : "bi-clock-fill"
                }`}
            ></i>
            {isCompleted ? "Service Completed" : "Service In Progress"}
          </small>
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Total Cost"
            name="totalCost"
            value={item?.totalCost || ""}
            disabled={item?.serviceCompleted}
            type="number"
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "totalCost", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Next Service Due"
            name="nextServiceDue"
            value={item?.nextServiceDue || ""}
            disabled={item?.serviceCompleted}
            type="date"
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "nextServiceDue", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Work Performed"
            name="workPerformed"
            value={item?.workPerformed || ""}
            disabled={item?.serviceCompleted}
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "workPerformed", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Invoice Number"
            name="invoiceNumber"
            value={item?.invoiceNumber || ""}
            disabled={item?.serviceCompleted}
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "invoiceNumber", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <InputField
            className="inputBox"
            label="Service Completed Date"
            name="serviceCompletedDate"
            value={item?.serviceCompletedDate || ""}
            disabled={item?.serviceCompleted}
            type="date"
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "serviceCompletedDate", e.target.value)
            }
          />
        </Col>

        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
          <TextAreaField
            className="inputBox"
            label="Remarks"
            name="remarks"
            value={item?.remarks || ""}
            disabled={item?.serviceCompleted}
            onChange={(e) =>
              onChangeServiceInfo(item?.serviceVehicleId, "remarks", e.target.value)
            }
          />
        </Col>

        {/* Attachment — upload only when not completed */}
        {!item?.serviceCompleted && (
          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
            <FileUploadComponent
              label="Service Attachment"
              id={`erfServiceAttachment_${item?.serviceVehicleId}`}
              name={`erfServiceAttachment_${item?.serviceVehicleId}`}
              onSuccessUpload={(data) => onUploadSuccess(item?.serviceVehicleId, data)}
              allowedTypes={["image/png", "image/jpeg"]}
              value={item?.erfServiceAttachment || ""}
            />
            <Col xs={2} md={2} lg={2} className="d-flex align-items-center mt-1">
              {item?.erfServiceAttachment && (
                <>
                  <img
                    src="assets/img/newIcons/attached-file.png"
                    className="attachmentIcon me-2"
                    alt="view attachment"
                    onClick={() => onViewFile(item?.erfServiceAttachment)}
                    style={{ cursor: "pointer" }}
                  />
                  <i
                    className="bi bi-trash text-danger fs-5"
                    onClick={() => onDeleteAttachment(item?.serviceVehicleId)}
                    style={{ cursor: "pointer" }}
                  />
                </>
              )}
            </Col>
          </Col>
        )}

        {/* Attachment — view only when completed */}
        {item?.serviceCompleted && item?.erfServiceAttachment && (
          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
            <label className="form-label fw-bold">Service Attachment</label>
            <Col xs={2} md={2} lg={2} className="d-flex align-items-center mt-1">
              <img
                src="assets/img/newIcons/attached-file.png"
                className="attachmentIcon me-2"
                alt="view attachment"
                onClick={() => onViewFile(item?.erfServiceAttachment)}
                style={{ cursor: "pointer" }}
              />
            </Col>
          </Col>
        )}

      </Row>
    </div>
  );
});

ServiceForm.displayName = "ServiceForm";

// ─────────────────────────────────────────────────────────────────────────────
// ERFService — main component
// ─────────────────────────────────────────────────────────────────────────────
const ERFServiceModal = React.forwardRef((props, ref) => {
  const { show, onHide, onSave, editMode, data, ...modalRest } = props;

  const supplierData = useSelector((state) => state?.supplier?.data);
  const dispatch = useDispatch();

  // ── Refs ─────────────────────────────────────────────────
  const serviceCounterRef = useRef(0);
  const serviceDetailsRef = useRef([]);

  // ── Local state ──────────────────────────────────────────
  const [serviceDetails, setServiceDetails] = useState([]);
  const [isCardVisible, setIsCardVisible] = useState(true);
  const [isCardVisible3, setIsCardVisible3] = useState(true);
  const [showFileViewModal, setShowFileViewModal] = useState(false);
  const [fileViewData, setFileViewData] = useState(null);

  // Sync ref with latest state
  serviceDetailsRef.current = serviceDetails;

  // ── Stable derived data ──────────────────────────────────
  const supplierDropdownData = useMemo(() => {
    if (!supplierData?.length) return [];
    return supplierData.map((item) => ({
      ...item,
      name: item.employeeName,
      code: item.AssociateNumber,
      description: item.employeeName,
    }));
  }, [supplierData]);

  const rentItems = useMemo(() => data?.rentItems || [], [data?.rentItems]);

  // ── lockedLocalIds ref ───────────────────────────────────
  // Built from all In Progress services' multi-selected IDs
  const lockedLocalIds = useRef(new Set());
  lockedLocalIds.current = new Set(
    serviceDetails
      .filter((s) => s.serviceStatus === SERVICE_STATUS.IN_PROGRESS)
      .flatMap((s) => (s.linkedLocalIds || []).map(String))
  );

  // ── Per-item service counts ──────────────────────────────
  const serviceCountByLocalId = useMemo(() => {
    return serviceDetails.reduce((acc, s) => {
      (s.linkedLocalIds || []).forEach((id) => {
        const key = String(id);
        acc[key] = (acc[key] || 0) + 1;
      });
      return acc;
    }, {});
  }, [serviceDetails]);

  // ── Fetch suppliers when modal opens ─────────────────────
  useEffect(() => {
    if (!show) return;
    dispatch(getSupplierList({
      page: 1,
      per_page: 999999,
      searchTerm: "",
      timeZone: "Asia/Calcutta",
      rowFilter: {
        $and: [
          { "vendorCategory.vendorCategoryCode": "V06" },
          { "vendorCategory.vendorCategoryDesc": { $ne: "Supplier" } },
        ],
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ── Seed / restore state when modal opens ────────────────
  useEffect(() => {
    if (!show) return;

    setIsCardVisible(true);
    setIsCardVisible3(true);
    setShowFileViewModal(false);
    setFileViewData(null);

    const savedHistory = data?.serviceHistory;
    if (savedHistory?.length) {
      // Ensure legacy single-select data is normalised to arrays
      const normalised = savedHistory.map((s) => ({
        ...s,
        linkedLocalIds: s.linkedLocalIds?.length
          ? s.linkedLocalIds
          : s.linkedLocalId
            ? [String(s.linkedLocalId)]
            : [],
        linkedRentItems: s.linkedRentItems || [],
      }));
      setServiceDetails(normalised);
      serviceCounterRef.current = normalised.length;
    } else {
      const id = 1;
      serviceCounterRef.current = id;
      setServiceDetails([{
        ...serviceInfoInitialData,
        serviceId: `SERVICE-${id}`,
        serviceVehicleId: id,
        serviceCreated: true,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ── Stable callbacks ─────────────────────────────────────
  const toggleCardVisibility = useCallback(() => setIsCardVisible((p) => !p), []);
  const toggleCardVisibility3 = useCallback(() => setIsCardVisible3((p) => !p), []);
  const handleViewFile = useCallback((id) => { setFileViewData(id); setShowFileViewModal(true); }, []);
  const handleViewFileHide = useCallback(() => setShowFileViewModal(false), []);

  // Add service form
  const addServiceForm = useCallback(() => {
    const current = serviceDetailsRef.current;

    const hasUnlinkedForm = current.some(
      (s) => s.serviceCreated === true && (!s.linkedLocalIds || s.linkedLocalIds.length === 0)
    );
    if (hasUnlinkedForm) {
      toast.error("Please select equipment for the existing service form before adding a new one.");
      return;
    }

    const id = ++serviceCounterRef.current;
    setServiceDetails((prev) => [
      {
        ...serviceInfoInitialData,
        serviceId: `SERVICE-${id}`,
        serviceVehicleId: id,
        serviceCreated: true,
      },
      ...prev,
    ]);
  }, []);

  const removeServiceForm = useCallback((serviceIdToRemove) => {
    setServiceDetails((prev) => prev.filter((f) => f.serviceId !== serviceIdToRemove));
  }, []);

  const handleChangeServiceInfo = useCallback((serviceVehicleId, key, value) => {
    setServiceDetails((prev) =>
      prev.map((item) =>
        item.serviceVehicleId === serviceVehicleId ? { ...item, [key]: value } : item
      )
    );
  }, []);

  const handleChangeManyServiceInfo = useCallback((serviceVehicleId, fields) => {
    setServiceDetails((prev) =>
      prev.map((item) =>
        item.serviceVehicleId === serviceVehicleId ? { ...item, ...fields } : item
      )
    );
  }, []);

  // Multi-select: add one equipment to a service form
  const handleSelectRentItem = useCallback((serviceVehicleId, selectedLocalId) => {
    setServiceDetails((prev) => {
      const selected = rentItems.find((r) => String(r.local_id) === String(selectedLocalId));
      if (!selected) return prev;

      return prev.map((item) => {
        if (item.serviceVehicleId !== serviceVehicleId) return item;

        const currentIds = item.linkedLocalIds || [];
        if (currentIds.includes(String(selected.local_id))) return item; // already selected

        const newIds = [...currentIds, String(selected.local_id)];
        const newRentItems = [...(item.linkedRentItems || []), selected];

        return {
          ...item,
          linkedLocalIds: newIds,
          linkedRentItems: newRentItems,
          linkedLocalId: newIds[0],                                          // first for compat
          linkedBarcode: newRentItems.map((r) => r.barcode).join(", "),
          linkedMaterialDescription: newRentItems.map((r) => r.materialDescription).join(", "),
          linkedEquipmentRentNr: newRentItems[0]?.equipmentRentNr || "",
        };
      });
    });
  }, [rentItems]);

  // Multi-select: remove one equipment chip from a service form
  const handleDeselectRentItem = useCallback((serviceVehicleId, localIdToRemove) => {
    setServiceDetails((prev) =>
      prev.map((item) => {
        if (item.serviceVehicleId !== serviceVehicleId) return item;

        const newIds = (item.linkedLocalIds || []).filter((id) => id !== String(localIdToRemove));
        const newRentItems = (item.linkedRentItems || []).filter(
          (r) => String(r.local_id) !== String(localIdToRemove)
        );

        return {
          ...item,
          linkedLocalIds: newIds,
          linkedRentItems: newRentItems,
          linkedLocalId: newIds[0] || null,
          linkedBarcode: newRentItems.map((r) => r.barcode).join(", "),
          linkedMaterialDescription: newRentItems.map((r) => r.materialDescription).join(", "),
          linkedEquipmentRentNr: newRentItems[0]?.equipmentRentNr || "",
        };
      })
    );
  }, []);

  const onSuccessUploadServiceAttachment = useCallback((serviceVehicleId, uploadData) => {
    const fileName = uploadData?.results?.[0]?.newFilename || uploadData?.results?.[0]?.fileName || "";
    const attachmentId = uploadData?.attachmentId || "";
    if (!fileName || !attachmentId) { toast.error("Upload failed: Missing file details."); return; }
    setServiceDetails((prev) =>
      prev.map((item) =>
        item.serviceVehicleId === serviceVehicleId
          ? { ...item, erfServiceAttachment: attachmentId, erfServiceAttachmentFileName: fileName }
          : item
      )
    );
    toast.success("Attachment uploaded successfully");
  }, []);

  const handleDeleteServiceAttachment = useCallback(async (serviceVehicleId) => {
    const serviceItem = serviceDetailsRef.current.find((s) => s.serviceVehicleId === serviceVehicleId);
    if (!serviceItem?.erfServiceAttachment) { toast.error("No attachment to delete"); return; }
    try {
      const response = await ItemService.deleteFile({
        attachmentId: serviceItem.erfServiceAttachment,
        fileName: serviceItem.erfServiceAttachmentFileName,
      });
      if (response?.message) {
        setServiceDetails((prev) =>
          prev.map((item) =>
            item.serviceVehicleId === serviceVehicleId
              ? { ...item, erfServiceAttachment: "", erfServiceAttachmentFileName: "" }
              : item
          )
        );
        toast.success("Attachment deleted successfully");
      } else {
        toast.error("Failed to delete attachment");
      }
    } catch (err) {
      console.error("Error deleting attachment:", err);
      toast.error("Error deleting attachment");
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    const current = serviceDetailsRef.current;

    // Validate: every new form must have at least one equipment selected
    const hasUnlinked = current.some(
      (s) => s.serviceCreated === true && (!s.linkedLocalIds || s.linkedLocalIds.length === 0)
    );
    if (hasUnlinked) {
      toast.error("Please select equipment for all service forms before submitting.");
      return;
    }

    const countByBarcode = current.reduce((acc, s) => {
      (s.linkedRentItems || []).forEach((r) => {
        if (r.barcode) acc[r.barcode] = (acc[r.barcode] || 0) + 1;
      });
      return acc;
    }, {});

    // onSave?.({
    //   serviceHistory: current.map((s) => ({
    //     ...s,
    //     serviceUpdate:    true,
    //     serviceCompleted: s.serviceStatus === SERVICE_STATUS.COMPLETED,
    //   })),
    //   serviceCountByBarcode: countByBarcode,
    //   rentItems: rentItems.map((item) => ({
    //     ...item,
    //     serviceCount: countByBarcode[item.barcode] || 0,
    //   })),
    //   totalServiceCount: current.length,
    // });

    onSave?.({
      serviceHistory: current.map((s) => ({
        ...s,
        serviceUpdate: true,
        serviceCompleted: s.serviceStatus === SERVICE_STATUS.COMPLETED,
        multipleMaterials: (s.linkedRentItems || []).map((r) => ({
          code: r.barcode,
          name: r.materialDescription,
        })),
      })),
      serviceCountByBarcode: countByBarcode,
      rentItems: rentItems.map((item) => ({
        ...item,
        serviceCount: countByBarcode[item.barcode] || 0,
      })),
      totalServiceCount: current.length,
    });
    onHide?.();
  }, [rentItems, onSave, onHide]);

  // ── Derived display values ────────────────────────────────
  const newServiceForms = useMemo(
    () => serviceDetails.filter((item) => item?.serviceCreated === true),
    [serviceDetails]
  );
  const totalServiceCount = serviceDetails.length;


  // ── Render ───────────────────────────────────────────────
  return (
    <Modal
      {...modalRest}
      show={show}
      onHide={onHide}
      aria-labelledby="contained-modal-title-vcenter"
      keyboard={false}
      size="xl"
      ref={ref}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Services</Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {/* ── Per-item service count summary bar ─────────────── */}
        {rentItems.length > 0 && (
          <Row className="mb-3">
            <Col xs={12}>
              <div
                className="d-flex flex-wrap gap-2 p-2"
                style={{ background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}
              >
                <span className="fw-bold me-2 align-self-center">Services per Item:</span>
                {rentItems.map((item) => {
                  const count = serviceCountByLocalId[String(item.local_id)] || 0;
                  return (
                    <div
                      key={item.local_id}
                      className="d-flex align-items-center gap-1"
                      style={{
                        background: "#fff",
                        border: "1px solid #dee2e6",
                        borderRadius: "6px",
                        padding: "4px 10px",
                        fontSize: "13px",
                      }}
                    >
                      <span className="text-muted" style={{ fontSize: "11px" }}>{item.barcode}</span>
                      <span className="fw-bold mx-1">{item.materialDescription}</span>
                      <span className={`badge ${count > 0 ? "bg-success" : "bg-secondary"}`}>
                        {count} {count === 1 ? "service" : "services"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        )}

        <Row className="py-2">
          {!editMode && (
            <Col xs={{ span: 2, offset: 10 }} md={{ span: 2, offset: 10 }} lg={{ span: 2, offset: 10 }}>
              <button className="btnTable" onClick={addServiceForm}>
                <i className="bi bi-plus-lg"></i> Add Service
              </button>
            </Col>
          )}
        </Row>

        {/* ── Service Information accordion ───────────────────── */}
        <div className="accordion mb-4" id="accordionExample">
          <div className="accordion-item">
            <h2 className="accordion-header">
              <Row className="accordion-button">
                <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                  <i
                    className={`bi ${isCardVisible ? "bi-chevron-right" : "bi-chevron-down"}`}
                    onClick={toggleCardVisibility}
                  ></i>
                </Col>
                <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                  <span className="iconLogo">SI</span>
                </Col>
                <Col xs={4} md={4} lg={4}>
                  <span className="headerTitle">Service Information</span>
                  <span className="subHeaderTitle">Service</span>
                </Col>
                <Col xs={6} md={6} lg={6} className="d-flex gap-2 align-items-center justify-content-end">
                  <span className="badge bg-primary">
                    {rentItems.length} Item{rentItems.length !== 1 ? "s" : ""}
                  </span>
                  <span className="badge bg-success">
                    {totalServiceCount} Service{totalServiceCount !== 1 ? "s" : ""}
                  </span>
                </Col>
              </Row>
            </h2>

            <div className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}>
              <div className="accordion-body">
                {newServiceForms.length > 0 ? (
                  newServiceForms.map((item) => (
                    <ServiceForm
                      key={item.serviceVehicleId}
                      item={item}
                      rentItems={rentItems}
                      editMode={editMode}
                      supplierDropdownData={supplierDropdownData}
                      lockedLocalIds={lockedLocalIds}
                      onSelectRentItem={handleSelectRentItem}
                      onDeselectRentItem={handleDeselectRentItem}
                      onChangeServiceInfo={handleChangeServiceInfo}
                      onChangeManyServiceInfo={handleChangeManyServiceInfo}
                      onRemoveService={removeServiceForm}
                      onUploadSuccess={onSuccessUploadServiceAttachment}
                      onDeleteAttachment={handleDeleteServiceAttachment}
                      onViewFile={handleViewFile}
                    />
                  ))
                ) : (
                  <p className="d-flex justify-content-center py-2 my-4">
                    Service not created
                  </p>
                )}
              </div>
            </div>

            {/* ── Service History accordion ─────────────────────── */}
            <div className="accordion my-4" id="accordionExample2">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${isCardVisible3 ? "bi-chevron-right" : "bi-chevron-down"}`}
                        onClick={toggleCardVisibility3}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">SH</span>
                    </Col>
                    <Col xs={6} md={6} lg={6}>
                      <span className="headerTitle">Service History</span>
                      <span className="subHeaderTitle">Service History Details</span>
                    </Col>
                    <Col xs={4} md={4} lg={4} className="d-flex gap-2 align-items-center justify-content-end flex-wrap">
                      {rentItems.map((item) => {
                        const count = serviceCountByLocalId[String(item.local_id)] || 0;
                        return (
                          <span
                            key={item.local_id}
                            className={`badge ${count > 0 ? "bg-success" : "bg-secondary"}`}
                            title={item.materialDescription}
                            style={{ fontSize: "11px" }}
                          >
                            {item.barcode}: {count}
                          </span>
                        );
                      })}
                    </Col>
                  </Row>
                </h2>

                <div className={`accordion-collapse collapse show ${isCardVisible3 ? "visible" : ""}`}>
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12} md={12} lg={12}>
                        <DataTable
                          title=""
                          columns={serviceHistoryColumns}
                          data={serviceDetails}
                          pagination
                          selectableRows
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </Modal.Body>

      {!editMode && (
        <Modal.Footer>
          <button onClick={handleSubmit} className="btn btn-primary">Submit</button>
          <button onClick={onHide} className="btn btn-danger">Cancel</button>
        </Modal.Footer>
      )}

      <FileViewerModal
        show={showFileViewModal}
        onHide={handleViewFileHide}
        fileid={fileViewData}
      />
    </Modal>
  );
});

ERFServiceModal.displayName = "ERFService";
export default ERFServiceModal;
